import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import AppFooter from "@/components/AppFooter";
import Reveal from "@/components/Reveal";

// Slugify utility for section anchors
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Renders one body item: plain string, ["list", [...]] tuple, ["quote", "..."] tuple, or raw JSX
function BodyBlock({ item, i }) {
  if (Array.isArray(item)) {
    const [kind, payload] = item;
    if (kind === "list") {
      return (
        <ul className="my-4 md:my-5 space-y-2.5 border-l-2 border-[#FFC53D]/70 pl-5">
          {payload.map((li) => (
            <li key={typeof li === "string" ? li : JSON.stringify(li)} className="text-[15px] md:text-base leading-[1.65] text-slate-700 relative">
              <span className="absolute -left-[27px] top-[10px] h-1.5 w-1.5 rounded-full bg-slate-400" />
              {li}
            </li>
          ))}
        </ul>
      );
    }
    if (kind === "quote") {
      return (
        <blockquote className="my-5 md:my-6 pl-5 border-l-2 border-slate-900 text-slate-900 font-heading font-light text-lg md:text-xl italic leading-[1.5]">
          {payload}
        </blockquote>
      );
    }
    if (kind === "callout") {
      return (
        <div className="my-5 md:my-6 border border-slate-200 bg-white p-5 md:p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 h-1 w-16 bg-[#FFC53D]" aria-hidden="true" />
          <p className="text-[13px] md:text-sm text-slate-700 leading-relaxed">{payload}</p>
        </div>
      );
    }
  }
  if (typeof item === "string") {
    return (
      <p className="text-[15px] md:text-base leading-[1.75] text-slate-700 mb-4 max-w-prose">
        {item}
      </p>
    );
  }
  return item;
}

export function ContentShell({
  eyebrow,
  h1,
  intro,
  sections,
  cta = { to: "/calculator", label: "Calculate my press release impact" },
}) {
  return (
    <div className="min-h-screen bg-[#FFFDF6] text-slate-900">
      <SiteHeader />

      <main className="max-w-6xl mx-auto px-5 md:px-8 lg:px-12 py-14 md:py-20">
        {/* Hero */}
        <Reveal>
          <p className="text-[11px] md:text-xs tracking-[0.32em] uppercase text-orange-600 font-semibold mb-4">
            {eyebrow}
          </p>
          <h1 className="font-heading font-light text-[38px] leading-[1.05] sm:text-5xl lg:text-6xl tracking-tight max-w-3xl mb-6 md:mb-8">
            {h1}
          </h1>
          <p className="text-base md:text-lg text-slate-600 leading-[1.6] max-w-2xl mb-10 md:mb-16">
            {intro}
          </p>
        </Reveal>

        {/* Body — grid: TOC sidebar on lg+, single column below */}
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10 lg:gap-16">
          {/* Sticky mini-TOC (lg+) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400 font-semibold mb-5">
                On this page
              </p>
              <ol className="space-y-4 border-l border-slate-200 pl-4">
                {sections.map((s, idx) => (
                  <li key={s.h2} className="relative">
                    <a
                      href={`#${slug(s.h2)}`}
                      data-testid={`toc-${slug(s.h2)}`}
                      className="group flex flex-col gap-0.5 text-[12px] leading-snug text-slate-500 hover:text-slate-900 transition-colors"
                    >
                      <span className="font-mono text-[10px] text-slate-400 group-hover:text-orange-600 transition-colors tracking-wider">
                        0{idx + 1}
                      </span>
                      <span className="tracking-wide">{s.h2}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Sections */}
          <div className="min-w-0">
            {sections.map((s, idx) => (
              <Reveal key={s.h2} delay={idx * 0.04} as="section">
                <section
                  id={slug(s.h2)}
                  className="mb-14 md:mb-16 scroll-mt-24 first:mt-0"
                >
                  <div className="flex items-center gap-3 mb-5 md:mb-6">
                    <span className="inline-grid place-items-center h-6 w-8 bg-slate-900 text-[10px] font-mono text-white tracking-wider">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <h2 className="font-heading text-[26px] md:text-[32px] font-medium tracking-tight text-slate-900 leading-tight">
                      {s.h2}
                    </h2>
                  </div>
                  <div>
                    {s.body.map((p, i) => {
                      // Body items are static (strings or tuples) and never reordered,
                      // so a section-scoped composite key is stable and unique.
                      const key = typeof p === "string"
                        ? `${slug(s.h2)}-p-${i}-${p.slice(0, 20)}`
                        : `${slug(s.h2)}-${p[0]}-${i}`;
                      return <BodyBlock key={key} item={p} i={i} />;
                    })}
                  </div>
                </section>
              </Reveal>
            ))}
          </div>
        </div>

        {/* CTA card */}
        <Reveal delay={0.05}>
          <section className="border border-slate-900 bg-slate-900 text-white p-6 md:p-10 lg:p-12 mt-8 md:mt-12 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#FFC53D]/30 blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-[#FFC53D]/15 blur-3xl" aria-hidden="true" />
            <Sparkles className="relative h-5 w-5 text-[#FFC53D] mb-3" strokeWidth={1.5} />
            <h2 className="relative font-heading text-2xl md:text-3xl lg:text-4xl font-light tracking-tight mb-3 max-w-xl">
              Ready to try it on your next release?
            </h2>
            <p className="relative text-sm md:text-base text-slate-300 max-w-lg mb-6 md:mb-8 leading-relaxed">
              Free tier · no card required · one full report emailed to you.
            </p>
            <Link to={cta.to} className="relative inline-block group">
              <Button
                size="lg"
                className="bg-[#FFC53D] hover:bg-[#FFD866] text-slate-900 rounded-sm px-6 py-6 text-sm md:text-base font-semibold tracking-wide shadow-[4px_4px_0_0_#0A0A0A] hover:shadow-[2px_2px_0_0_#0A0A0A] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              >
                {cta.label}
                <ArrowUpRight className="h-5 w-5 ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Button>
            </Link>
          </section>
        </Reveal>
      </main>
      <AppFooter />
    </div>
  );
}

export function HowItWorks() {
  return (
    <ContentShell
      eyebrow="How it works"
      h1="How PressImpact works"
      intro="PressImpact is a transparent calculator, not an oracle. It takes six campaign inputs, combines them with sourced industry benchmarks, and produces a boardroom-ready estimate of reach, media value, and potential ROI — with every number traceable to its origin."
      sections={[
        {
          h2: "What you enter",
          body: [
            "Quick mode asks for six numbers:",
            ["list", [
              "Campaign budget",
              "Baseline monthly traffic",
              "Expected traffic lift (%)",
              "Conversion rate",
              "Average deal or order value",
              "Timeframe (months)",
            ]],
            "Everything else — industry CPC, media pickup rate, backlink value, CPM — pre-fills from public benchmarks.",
            ["callout", "Advanced mode exposes 20+ underlying assumptions for override. Each input is labelled as user-provided so any auditor can tell your assumptions apart from PressImpact defaults."],
          ],
        },
        {
          h2: "How costs are modelled",
          body: [
            "Total campaign cost is the plain sum of six categories:",
            ["list", [
              "Writing",
              "Agency / labour",
              "Distribution",
              "Design",
              "Paid amplification",
              "Tracking",
            ]],
            ["quote", "No opaque overhead. No hidden multipliers. The cost figure is a user input — nothing is estimated on this side of the equation."],
          ],
        },
        {
          h2: "How reach and media value are estimated",
          body: [
            "Expected impressions are drawn from the wire tier you choose and multiplied by an industry-average pickup rate.",
            "Media value is expressed as a modelled equivalent of paid CPM — labelled clearly on the report as a modelled value, not verified revenue.",
            "Backlink value is computed from expected links × the DA-band benchmark for link value.",
          ],
        },
        {
          h2: "How scenarios and ranges work",
          body: [
            "PressImpact flexes the three most sensitive inputs — expected lift, conversion rate, and media pickup — by ±30%.",
            "That produces three scenarios: conservative, base, and optimistic.",
            ["callout", "This replaces false-precision single-point forecasts. You always see where the number could realistically land."],
          ],
        },
        {
          h2: "What the calculator cannot prove",
          body: [
            "PressImpact does not promise pickup, does not guarantee traffic, and never treats modelled media value as cash revenue.",
            "It is a decision-support tool for the pre-publish stage.",
            ["quote", "Post-campaign attribution requires verified analytics that live outside the model."],
          ],
        },
        {
          h2: "How the final report is structured",
          body: [
            "The report has two readable layers:",
            ["list", [
              "CMO scorecard (top) — cost efficiency, share of voice, media reach",
              "CFO detail (below) — methodology, sourced benchmarks, sensitivity, ECB currency provenance, assumptions table",
            ]],
            "Both layers ship in a shareable read-only link and print cleanly to PDF.",
          ],
        },
      ]}
    />
  );
}

export function ForCMOs() {
  return (
    <ContentShell
      eyebrow="For CMOs"
      h1="A clearer business case for your next press release"
      intro="Before you commit budget to a press release, you need a defensible answer to one question: what is this likely to move? PressImpact gives marketing leaders a pre-launch view of reach, share of voice, and business impact — sharp enough to take into a leadership review, honest enough that the CFO won't push back."
      cta={{ to: "/calculator", label: "Build a CMO-ready impact report" }}
      sections={[
        {
          h2: "Evaluate a release before you launch",
          body: [
            "Enter six inputs — budget, baseline traffic, expected lift, conversion rate, deal size, timeframe.",
            "PressImpact estimates reach, media value, and revenue across three scenarios: conservative, base, and optimistic.",
            ["callout", "No black-box AI. No unnamed benchmarks. Every figure links back to a public source or an assumption you can override."],
          ],
        },
        {
          h2: "Reach and share of voice at a glance",
          body: [
            "The top CMO scorecard on every report shows three headline metrics:",
            ["list", [
              "Media reach — impressions delivered after pickup weighting",
              "Share of voice — delivered reach as a multiple of your monthly organic traffic",
              "Cost efficiency — benefit per currency unit spent",
            ]],
            "One sentence under each explains what the number means in plain language.",
          ],
        },
        {
          h2: "Message and distribution opportunities",
          body: [
            "The report flags the three inputs the estimate is most sensitive to — usually pickup, conversion, and traffic lift.",
            ["list", [
              "Pickup is the swing driver → stronger angle or higher-tier wire",
              "Conversion is the swing driver → landing page work",
              "Lift is the swing driver → distribution or paid amplification",
            ]],
            ["quote", "The model becomes a working brief — not a passive forecast."],
          ],
        },
        {
          h2: "Explain the result to leadership",
          body: [
            "Every report generates a short executive summary in plain UK English with a clear Approve / Revise / Reject recommendation.",
            "The narrative is bounded — it interprets the numbers, never invents them.",
            ["callout", "Every report also has a Details for finance layer beneath the scorecard — sourced benchmarks, ECB currency provenance, full assumptions table. It's the page you send to your CFO."],
          ],
        },
        {
          h2: "Three actions that improve the number",
          body: [
            "The report closes with the three inputs with the largest impact on ROI in your specific model.",
            ["list", [
              "Rewrite the angle to increase pickup",
              "Move to premium distribution to widen impressions",
              "Optimise the landing-page CTA to lift conversion",
            ]],
            "Each recommended action is tied to the actual sensitivity of your numbers — not a generic best-practice list.",
          ],
        },
      ]}
    />
  );
}

export function ForFinance() {
  return (
    <ContentShell
      eyebrow="Show your CFO"
      h1="The page you send to your CFO"
      intro="You've built the case for the release. Now you need finance to sign it off. This is the read-only page you share with them — every cost, every benchmark, every formula, every currency conversion, printed with its source and date. Not a black box. Not marketing spin."
      cta={{ to: "/calculator", label: "Build a report your CFO can inspect" }}
      sections={[
        {
          h2: "What finance will look at first",
          body: [
            "The CFO doesn't need marketing language. They need three answers:",
            ["list", [
              "Total cost, total benefit, net benefit, ROI %",
              "Break-even leads and payback period in months",
              "Sensitivity range — conservative, base, optimistic",
            ]],
            ["callout", "Payback beyond the campaign timeframe is flagged explicitly with an amber warning. No false precision."],
          ],
        },
        {
          h2: "Cost model — plain arithmetic",
          body: [
            "Total campaign cost = plain sum of six categories:",
            ["list", [
              "Writing",
              "Agency / labour",
              "Distribution",
              "Design",
              "Paid amplification",
              "Tracking",
            ]],
            ["quote", "No opaque overhead multipliers. Every line item is inspectable."],
          ],
        },
        {
          h2: "Benefit model — sourced, ranged, honest",
          body: [
            "Total benefit = sum of five modelled components:",
            ["list", [
              "Incremental traffic value (Δ visits × CPC benchmark)",
              "Lead value (Δ leads × cost-per-lead benchmark)",
              "Revenue (Δ leads × close rate × AOV × gross margin)",
              "SEO / backlink value",
              "Modelled media value",
            ]],
            ["callout", "If a required input is missing, the model uses a labelled industry-average benchmark and reduces the confidence signal. Every substituted default is printed as 'estimated (benchmark)' on the assumptions table."],
          ],
        },
        {
          h2: "Media value is never revenue",
          body: [
            "PressImpact labels earned media value as a modelled equivalent of paid CPM — not verified revenue.",
            "The scorecard, benefit breakdown, and executive summary all use language that keeps this boundary visible.",
            ["callout", "Media value tells you what comparable paid coverage would cost. It does not enter the revenue line unless your business model treats it that way."],
          ],
        },
        {
          h2: "Currency provenance and audit trail",
          body: [
            "Live European Central Bank reference rates for USD, EUR, and NOK, refreshed every six hours.",
            ["list", [
              "Every report prints the ECB rate date in the footer",
              "Original currency is stored separately from display currency — historical figures never change if today's rate moves",
              "Every benchmark row prints its source, publication date, and licence status",
            ]],
            ["quote", "Everything the CFO would ask about is already on the page."],
          ],
        },
      ]}
    />
  );
}

export function Benchmarks() {
  return (
    <ContentShell
      eyebrow="Benchmarks"
      h1="Transparent PR benchmarks for Nordic and international teams"
      intro="Every benchmark PressImpact uses is public, dated, and sourced. Nothing is a black box. Nordic-specific data is used where written commercial permission is confirmed; otherwise a clearly-labelled European or global fallback is applied."
      sections={[
        {
          h2: "What a benchmark is",
          body: [
            "A benchmark is a reference value drawn from public industry research.",
            ["list", [
              "WordStream's median Google Ads CPC for SaaS",
              "HubSpot's cost-per-lead by industry",
              "Cision / Meltwater median pickup rates by wire tier",
            ]],
            "PressImpact uses benchmarks to pre-fill the parts of the model where you probably don't have your own data.",
            ["callout", "You can override any benchmark at any time. Overrides are labelled 'user input' on the report."],
          ],
        },
        {
          h2: "Nordic versus global fallback",
          body: [
            "When you select a Nordic market and a licensed Nordic benchmark exists, PressImpact uses it and labels it as a Nordic benchmark.",
            "When no country-specific data is licensed, PressImpact falls back to the European or global average.",
            ["callout", "The report prints a visible notice: \"No country-specific benchmark was available. A global fallback was used. Replace it with your own verified data for a more precise estimate.\""],
          ],
        },
        {
          h2: "Source, date, and licensing transparency",
          body: [
            "Every row in the Assumptions table shows:",
            ["list", [
              "Benchmark name and value",
              "Source and publication date",
              "Licence status",
            ]],
            "Sources are tagged as one of: public · licensed · PressImpact-modelled · user-provided · awaiting validation.",
            ["callout", "Nordic association data (Sveriges Kommunikatörer, ProCom Finland) is only presented as licensed when written commercial permission is confirmed — otherwise it appears with a 'Pending licence' tag."],
          ],
        },
        {
          h2: "How you can replace assumptions",
          body: [
            "The Calculator's Advanced mode exposes every benchmark as an editable field.",
            "Any override is stored on the report and printed alongside the default — future readers can see what changed.",
            ["quote", "Manual overrides are automatically labelled as user input rather than PressImpact-estimated."],
          ],
        },
      ]}
    />
  );
}
